/* eslint-disable max-lines */
import React from 'react'
import { CrossRefPaper, paperInitState } from '@cpsy-rmap-project/web-core/crossref'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useLocation, useHistory } from 'react-router-dom'
import { useUsecasePaper } from '../../../usecases/paper'
import { useLoading } from '../../atoms/Loading'
import { useLogin } from '../../organisms/Login'
// import { usePaper } from '../../organisms/Paper'
import { varidatePaper } from './varidation'

// eslint-disable-next-line no-shadow
export enum SubmitModelSteps {
  'FIND_PAPER' = 0,
  'SELECT_PAPER' = 1,
  'TAG_MODEL' = 2,
  'TAG_DISEASE' = 3,
  'TAG_RDOC' = 4,
  'INPUT_DETAILS' = 5,
  'FINISHED' = 6
}

interface DialogState {
  open: boolean
  activeStep: number
  isUseDoi: boolean
  isInputManually: boolean
  keywords: string
  // フラグ用
  isReadySearch: boolean
  isReadySubmit: boolean
}

interface Handle {
  handleResetPaper: () => void
  handleResetState: () => void
  handleOpen: () => void
  handleOpenWithKeywords: (keywords?: string) => void
  handleClose: () => void
  handleBack: () => void
  handleNext: (args?: { keywords?: string }) => Promise<void>
  handleSubmit: () => Promise<void>
  handleUseDOI: (bool: boolean) => void
  handleInputManually: () => void
  handleKeywords: (_keywords: string) => void
  handleSelectPaper: (_paper: CrossRefPaper) => void
  handleDetailsChange: (update: Partial<CrossRefPaper>) => void
  handleIsReadySearch: (_isReadySearch: boolean) => void
  handleIsReadySubmit: (_isReadySubmit: boolean) => void
}

const dialogInitState: DialogState = {
  open: false,
  activeStep: 0,
  isUseDoi: false,
  isInputManually: false,
  keywords: '',
  // フラグ用
  isReadySearch: false,
  isReadySubmit: false
}

const handleInitState: Handle = {
  handleResetPaper: () => {},
  handleResetState: () => {},
  handleOpen: () => {},
  handleOpenWithKeywords: () => {},
  handleClose: () => {},
  handleBack: () => {},
  handleNext: async () => {},
  handleSubmit: async () => {},
  handleUseDOI: (_bool: boolean) => {},
  handleInputManually: () => {},
  handleKeywords: (_keywords: string) => {},
  handleSelectPaper: (_paper: CrossRefPaper) => {},
  handleDetailsChange: (_update: Partial<CrossRefPaper>) => {},
  handleIsReadySearch: (_isReadySearch: boolean) => {},
  handleIsReadySubmit: (_isReadySubmit: boolean) => {}
}

const SubmitModelDialogContext = React.createContext<
  { dialog: DialogState } & { paper: CrossRefPaper } & { handle: Handle }
>({
  dialog: dialogInitState,
  paper: paperInitState,
  handle: handleInitState
})

// eslint-disable-next-line max-lines-per-function
export const SubmitModelDialogProvider: React.FC = ({ children }) => {
  /**
   * Router系設定
   */
  const history = useHistory()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const QUERY_STRING_KEY = 'mode'
  const QUERY_STRING_VALUE = 'add'
  const queryStringsAddFlag = query.get(QUERY_STRING_KEY)

  // URLクエリを追加する(?QUERY_STRING_KEYを付与する)
  const addUrlParams = React.useCallback(() => {
    const nextquery = new URLSearchParams(location.search)
    nextquery.set(QUERY_STRING_KEY, QUERY_STRING_VALUE)
    history.push(`${location.pathname}?${nextquery.toString()}`)
  }, [history, location])

  // URLクエリを除外する(?QUERY_STRING_KEYを消す)
  const removeUrlParams = React.useCallback(() => {
    const nextquery = new URLSearchParams(location.search)
    nextquery.delete(QUERY_STRING_KEY)
    history.push(`${location.pathname}?${nextquery.toString()}`)
  }, [history, location])

  // URLから移動してきた場合モーダルをオープンする
  React.useEffect(() => {
    if (queryStringsAddFlag === QUERY_STRING_VALUE) {
      setOpen(true)
    } else setOpen(false)
  }, [queryStringsAddFlag])

  //

  // Paper State
  // const { handler } = usePaper()

  // Dialog State
  const [open, setOpen] = React.useState(dialogInitState.open)
  const [activeStep, setActiveStep] = React.useState(dialogInitState.activeStep)
  const [isUseDoi, setIsUseDoi] = React.useState<boolean>(dialogInitState.isUseDoi)
  const [isInputManually, setIsInputManually] = React.useState<boolean>(dialogInitState.isInputManually)
  const [keywords, setKeywords] = React.useState<string>(dialogInitState.keywords)
  const [isReadySearch, setIsReadySearch] = React.useState<boolean>(dialogInitState.isReadySearch)
  const [isReadySubmit, setIsReadySubmit] = React.useState<boolean>(dialogInitState.isReadySubmit)

  // Paper State
  const [paper, setPaper] = React.useState<CrossRefPaper>(paperInitState)

  // Firebase
  const login = useLogin()
  const firestorePaper = useUsecasePaper()

  // 通知用
  const { handleIsLoading } = useLoading()
  const snackbar = useSnackbar()

  const handleResetPaper = React.useCallback(() => {
    setPaper(paperInitState)
  }, [])

  const handleResetState = React.useCallback(() => {
    setActiveStep(SubmitModelSteps.FIND_PAPER)
    setIsUseDoi(false)
    setIsInputManually(false)
    setKeywords('')
    handleResetPaper()
    setIsReadySubmit(false)
  }, [handleResetPaper])

  const handleOpen = React.useCallback(() => {
    addUrlParams()
  }, [addUrlParams])

  const handleClose = React.useCallback(() => {
    removeUrlParams()
  }, [removeUrlParams])

  const handleIsReadySearch = React.useCallback((_isReadySearch: boolean) => {
    setIsReadySearch(_isReadySearch)
  }, [])

  const handleIsReadySubmit = React.useCallback((_isReadySubmit: boolean) => {
    setIsReadySubmit(_isReadySubmit)
  }, [])

  const handleBack = React.useCallback(() => {
    switch (activeStep) {
      case SubmitModelSteps.FIND_PAPER:
        handleClose()
        break
      case SubmitModelSteps.SELECT_PAPER:
        setActiveStep(SubmitModelSteps.FIND_PAPER)
        handleIsReadySearch(false)
        setKeywords('')
        break
      case SubmitModelSteps.TAG_MODEL:
        if (isInputManually) {
          setActiveStep(SubmitModelSteps.FIND_PAPER)
          break
        } else {
          setActiveStep(SubmitModelSteps.SELECT_PAPER)
          break
        }
      case SubmitModelSteps.TAG_DISEASE:
        setActiveStep(SubmitModelSteps.TAG_MODEL)
        break
      case SubmitModelSteps.TAG_RDOC:
        setActiveStep(SubmitModelSteps.TAG_DISEASE)
        break
      case SubmitModelSteps.INPUT_DETAILS:
        setActiveStep(SubmitModelSteps.TAG_RDOC)
        break
      case SubmitModelSteps.FINISHED:
        setActiveStep(SubmitModelSteps.INPUT_DETAILS)
        break
      default:
        // 想定外
        break
    }
  }, [activeStep, handleClose, handleIsReadySearch, isInputManually])

  const handleSubmit = React.useCallback(
    async () => {
      if (isReadySubmit) {
        const varidateErrors = varidatePaper(paper)
        if (!_.isEmpty(varidateErrors)) {
          varidateErrors.forEach(error => {
            snackbar.enqueueSnackbar(error, { variant: 'error' })
          })
          handleIsReadySubmit(false)
          handleBack()
          handleIsLoading(false)
        } else if (!login.isLoggedIn()) {
          login.handleOpen()
          snackbar.enqueueSnackbar('Require Login.', { variant: 'error' })
          handleIsReadySubmit(false)
          handleBack()
          handleIsLoading(false)
        } else {
          try {
            // create処理（DB登録＋内部Papers更新）
            /* const createdPaper = */ await firestorePaper.addPaper({
              ...paper,
              draftByAutoTag: false
            })
            // handler.create(createdPaper)
            //
            snackbar.enqueueSnackbar('Successfully submitted.', {
              variant: 'success'
            })
            setIsReadySubmit(false)
            handleClose()
            handleResetState()
          } catch (e) {
            console.error(e)
            snackbar.enqueueSnackbar('Oops! something went wrong.', {
              variant: 'error'
            })
            handleIsReadySubmit(false)
            handleBack()
          } finally {
            handleIsLoading(false)
          }
        }
      }
    },
    // snackbarとfirestorePaperとloginを無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleBack, handleClose, handleIsLoading, handleResetState, isReadySubmit, paper]
  )

  const handleNext: Handle['handleNext'] = React.useCallback(
    async _args => {
      switch (activeStep) {
        case SubmitModelSteps.FIND_PAPER: {
          setIsInputManually(false)
          setActiveStep(SubmitModelSteps.SELECT_PAPER)
          break
        }
        case SubmitModelSteps.SELECT_PAPER: {
          setIsInputManually(false)
          setActiveStep(SubmitModelSteps.TAG_MODEL)
          break
        }
        case SubmitModelSteps.TAG_MODEL: {
          setActiveStep(SubmitModelSteps.TAG_DISEASE)
          break
        }
        case SubmitModelSteps.TAG_DISEASE: {
          setActiveStep(SubmitModelSteps.TAG_RDOC)
          break
        }
        case SubmitModelSteps.TAG_RDOC: {
          setActiveStep(SubmitModelSteps.INPUT_DETAILS)
          break
        }
        case SubmitModelSteps.INPUT_DETAILS: {
          handleIsLoading(true)
          setActiveStep(SubmitModelSteps.FINISHED)
          break
        }
        default: {
          // 想定外
          break
        }
      }
    },
    // snackbarのみを無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeStep, handleClose, handleIsLoading, handleResetState, handleSubmit]
  )

  const handleUseDOI = React.useCallback((bool: boolean) => {
    setIsUseDoi(bool)
  }, [])

  const handleInputManually = React.useCallback(() => {
    setIsInputManually(true)
    setActiveStep(SubmitModelSteps.TAG_MODEL)
  }, [])

  const handleSelectPaper = React.useCallback((_paper: CrossRefPaper) => {
    setPaper(_paper)
  }, [])

  const handleDetailsChange = React.useCallback(
    (update: Partial<CrossRefPaper>) => {
      setPaper({ ...paper, ...update })
    },
    [paper]
  )
  const handleKeywords = React.useCallback(
    (_keywords: string) => {
      setKeywords(_keywords)
      handleIsReadySearch(true)
    },
    [handleIsReadySearch]
  )

  const handleOpenWithKeywords: Handle['handleOpenWithKeywords'] = _keywords => {
    handleResetState()
    if (_keywords) {
      setIsInputManually(false)
      setKeywords(_keywords)
      setIsReadySearch(true)
      setActiveStep(SubmitModelSteps.SELECT_PAPER)
    }
    handleOpen()
  }

  return (
    <SubmitModelDialogContext.Provider
      value={{
        dialog: {
          open,
          activeStep,
          isUseDoi,
          isInputManually,
          keywords,
          isReadySearch,
          isReadySubmit
        },
        paper,
        handle: {
          handleResetPaper,
          handleResetState,
          handleOpen,
          handleOpenWithKeywords,
          handleClose,
          handleBack,
          handleNext,
          handleSubmit,
          handleUseDOI,
          handleInputManually,
          handleKeywords,
          handleSelectPaper,
          handleDetailsChange,
          handleIsReadySearch,
          handleIsReadySubmit
        }
      }}
    >
      {children}
    </SubmitModelDialogContext.Provider>
  )
}

export const useSubmitModelDialog = () => {
  const { dialog, paper, handle } = React.useContext(SubmitModelDialogContext)
  return { dialog, paper, handle }
}
